import { getJSON, postJSON, type WalletSessionResponse, userWalletApiBase } from './api';
import { loadMpcWasm } from './mpcWasm';
import {
  clearMpcUnlockCache,
  getAccountPayPasswordPlain,
  getMpcUnlock,
  setMpcUnlock,
} from './mpcUnlockCache';
import { decryptBytesWithPaymentPassword } from './paymentCrypto';
import { sha256Utf8Hex } from './passwordWire';

export { clearMpcUnlockCache } from './mpcUnlockCache';
export { useWalletSessionUnlocked } from '../hooks/useWalletSessionUnlocked';

const LEGACY_SESS_PREFIX = 'cointrader_mpc_sess:';
const MPC_BLOB_PREFIX = 'cointrader_mpc_blob:';

type CachedMpcBlob = {
  wallet_id: number;
  encrypted_client_session: string;
  encrypted_client_secret?: string;
};

type ResolveSessionOpts = {
  forceRefresh?: boolean;
  ensureClientSecret?: boolean;
  sessionMissingMessage: string;
  sessionInvalidMessage: string;
};

type SignDigestOpts = {
  commonErrorMessage: string;
  sessionMissingMessage: string;
  sessionInvalidMessage: string;
  /** When the account has MFA enabled, the 6-digit TOTP from the authenticator app. */
  mfaCode?: string;
};

function legacySessStorageKey(walletId: number): string {
  return `${LEGACY_SESS_PREFIX}${walletId}`;
}

function blobStorageKey(walletId: number): string {
  return `${MPC_BLOB_PREFIX}${walletId}`;
}

function isRecoverEthStyleVError(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  return msg.includes('unable to recover eth-style v from signature');
}

function parseCachedMpcBlob(walletId: number): CachedMpcBlob | null {
  const raw = sessionStorage.getItem(blobStorageKey(walletId));
  if (!raw || !raw.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CachedMpcBlob;
    if (parsed.wallet_id !== walletId) {
      return null;
    }
    if (!parsed.encrypted_client_session?.trim()) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

async function fetchAndCacheMpcBlob(walletId: number, sessionMissingMessage: string): Promise<CachedMpcBlob> {
  const { res, data } = await getJSON<WalletSessionResponse>(`${userWalletApiBase(walletId)}/session`);
  if (!res.ok || !data.encrypted_client_session?.trim()) {
    throw new Error(data.error || sessionMissingMessage);
  }
  const blob: CachedMpcBlob = {
    wallet_id: walletId,
    encrypted_client_session: data.encrypted_client_session.trim(),
    encrypted_client_secret: data.encrypted_client_secret?.trim() || undefined,
  };
  sessionStorage.setItem(blobStorageKey(walletId), JSON.stringify(blob));
  return blob;
}

export async function resolveWalletSessionJson(walletId: number, payPwd: string, opts: ResolveSessionOpts): Promise<string> {
  const blobKey = blobStorageKey(walletId);
  const legacyKey = legacySessStorageKey(walletId);
  if (opts.forceRefresh) {
    clearMpcUnlockCache(walletId);
    sessionStorage.removeItem(blobKey);
    sessionStorage.removeItem(legacyKey);
  }

  const pwdTrim = payPwd.trim();
  if (!pwdTrim) {
    const cachedSession = (await getMpcUnlock(walletId))?.sessionJson?.trim();
    if (cachedSession) {
      return cachedSession;
    }
    const accountPlain = await getAccountPayPasswordPlain();
    if (!accountPlain) {
      throw new Error(opts.sessionMissingMessage);
    }
    let blob = parseCachedMpcBlob(walletId);
    if (!blob || (opts.ensureClientSecret && !blob.encrypted_client_secret?.trim())) {
      blob = await fetchAndCacheMpcBlob(walletId, opts.sessionMissingMessage);
    }
    try {
      return await decryptWalletSessionJson(walletId, blob, accountPlain, opts);
    } catch {
      clearMpcUnlockCache();
      throw new Error(opts.sessionMissingMessage);
    }
  }

  let blob = parseCachedMpcBlob(walletId);
  if (!blob || (opts.ensureClientSecret && !blob.encrypted_client_secret?.trim())) {
    blob = await fetchAndCacheMpcBlob(walletId, opts.sessionMissingMessage);
  }

  try {
    return await decryptWalletSessionJson(walletId, blob, pwdTrim, opts);
  } catch {
    // Payment password was rotated on another device/browser: sessionStorage still holds ciphertext
    // encrypted with the old password; fetch latest blobs from the server once and retry.
    sessionStorage.removeItem(blobKey);
    sessionStorage.removeItem(legacyKey);
    clearMpcUnlockCache(walletId);
    const fresh = await fetchAndCacheMpcBlob(walletId, opts.sessionMissingMessage);
    return await decryptWalletSessionJson(walletId, fresh, pwdTrim, opts);
  }
}

async function decryptWalletSessionJson(
  walletId: number,
  b: CachedMpcBlob,
  payPwd: string,
  opts: ResolveSessionOpts,
): Promise<string> {
  const legacyKey = legacySessStorageKey(walletId);
  const plain = await decryptBytesWithPaymentPassword(payPwd, b.encrypted_client_session);
  const sessionJson = new TextDecoder().decode(plain);
  const obj = JSON.parse(sessionJson) as { session_id?: string };
  if (!obj.session_id) {
    throw new Error(opts.sessionInvalidMessage);
  }
  sessionStorage.setItem(legacyKey, sessionJson);
  const payPwdWire = await sha256Utf8Hex(payPwd);
  await setMpcUnlock(walletId, { sessionJson, payPwdWire }, payPwd);
  return sessionJson;
}

export async function signDigestWithWalletMpc(
  walletId: number,
  digestHex: string,
  payPwd: string,
  opts: SignDigestOpts,
): Promise<{ r_hex: string; s_hex: string; v: number }> {
  const wasm = await loadMpcWasm();
  for (const forceRefresh of [false, true]) {
    try {
      await resolveWalletSessionJson(walletId, payPwd, {
        forceRefresh,
        ensureClientSecret: true,
        sessionMissingMessage: opts.sessionMissingMessage,
        sessionInvalidMessage: opts.sessionInvalidMessage,
      });
      const unlock = await getMpcUnlock(walletId);
      if (!unlock?.sessionJson?.trim()) {
        throw new Error(opts.sessionMissingMessage);
      }
      const payPwdWire = unlock.payPwdWire;
      const sessionJson = unlock.sessionJson;
      const round1Str = wasm.wasm_sign_round1(digestHex);
      const round1 = JSON.parse(round1Str) as { z_hex: string; k1_g_hex: string };
      const bodyRound2: Record<string, unknown> = {
        wallet_id: walletId,
        z_hex: round1.z_hex,
        k1_g_hex: round1.k1_g_hex,
        payment_password: payPwdWire,
      };
      const mc = opts.mfaCode?.trim();
      if (mc) {
        bodyRound2.mfa_code = mc;
      }
      const { res: r2Res, data: r2Data } = await postJSON<{ error?: string }>('/api/user/wallets/sign-round2', bodyRound2);
      if (!r2Res.ok) {
        console.error('[mpc-sign] sign-round2 failed', { walletId, forceRefresh, status: r2Res.status, data: r2Data });
        throw new Error(r2Data.error || opts.commonErrorMessage);
      }
      console.info('[mpc-sign] sign-round2 ok', { walletId, forceRefresh, round2Keys: Object.keys(r2Data as object) });
      let completeRaw: string;
      try {
        completeRaw = wasm.wasm_sign_complete(sessionJson, round1Str, JSON.stringify(r2Data));
      } catch (completeErr) {
        console.error('[mpc-sign] wasm_sign_complete threw', { walletId, forceRefresh, completeErr });
        throw completeErr;
      }
      let parsed: { r_hex: string; s_hex: string; v: number };
      try {
        parsed = JSON.parse(completeRaw) as { r_hex: string; s_hex: string; v: number };
      } catch (parseErr) {
        console.error('[mpc-sign] wasm_sign_complete JSON parse failed', { walletId, forceRefresh, completeRaw, parseErr });
        throw parseErr;
      }
      console.info('[mpc-sign] wasm_sign_complete ok', {
        walletId,
        forceRefresh,
        r_hex: parsed.r_hex,
        s_hex: parsed.s_hex,
        v: parsed.v,
        vType: typeof parsed.v,
      });
      return parsed;
    } catch (err) {
      console.error('[mpc-sign] attempt failed', { walletId, forceRefresh, err: String(err) });
      if (!forceRefresh && isRecoverEthStyleVError(err)) {
        console.warn('[mpc-sign] retrying after eth-style v recovery failure');
        continue;
      }
      throw err;
    }
  }
  throw new Error(opts.commonErrorMessage);
}

export async function signDigestRawWithWalletMpc(
  walletId: number,
  digestHex: string,
  payPwd: string,
  opts: SignDigestOpts,
): Promise<{ r_hex: string; s_hex: string }> {
  const { r_hex, s_hex } = await signDigestWithWalletMpc(walletId, digestHex, payPwd, opts);
  return { r_hex, s_hex };
}
